// Copyright (c) 2020-2022 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Grid } from '@mui/material';
import { RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { UnregisterCallback } from 'history';

import container from '@core/di';
import history from '@shared/utils/history';
import ROUTES from '@shared/constants/routes';
import OrdersStore from '@shared/stores/orders';
import AuthStore from '@core/stores/auth';
import { NotificationType, showNotification } from '@shared/components/Notification';
import { HeaderConfig } from '@modules/Private/components/Header';
import OrderDetails from './steps/OrderDetails';
import AssetDeliverables from './steps/AssetDeliverables';
import Stepper, { StepConfig } from './components/Stepper';
import { getBreadCrumbs, deleteQueries, errObj2Str } from '@shared/utils/common';

import Tasks from '@shared/components/Tasks';
import { OrderTask } from '@shared/models/ordertask';

// import InformationTypeStyles from '@modules/Private/submodules/DataManagement/pages/DataManagementChoice/components/InformationType/InformationType.styles';
export enum StepNumber {
  orderDetails = 1,
  assetsDeliverables = 2,
}

export const FIRST_STEP = StepNumber.orderDetails;
// No longer require 1 asset & 1 deliverable
//const LAST_STEP = StepNumber.assetsDeliverables;
const LAST_STEP = StepNumber.orderDetails;

export interface OrderCreatingProps extends RouteComponentProps {
  className?: string;
  setHeaderConfig: (config: HeaderConfig) => any;
}

@observer
class OrderCreating extends React.Component<OrderCreatingProps> {
  @observable isSubmitting = false;
  @observable showTasks: boolean = false; // Flag for displaying Tasks dialog. Also use for triggering a render
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  private unblock: UnregisterCallback;
  private selectedDelis: OrderTask[];
  private steps: Array<StepConfig> = [
    { number: StepNumber.orderDetails, name: 'Order details' },
    //{ number: StepNumber.assetsDeliverables, name: 'Assets & Deliverables' },
  ];

  constructor(props: OrderCreatingProps) {
    super(props);
    this.showTasks = false;
    props.setHeaderConfig({
      moduleName: 'Add order',
    });

    this.ordersStore.initializeForm();
    
    deleteQueries();
    makeObservable(this);
  }

  componentWillUnmount() {
    this.ordersStore.resetManagement();
    this.unblock();
  }

  componentDidMount() {
    this.initializeLeaveConfirmation();
  }

  private initializeLeaveConfirmation = () => {
    this.unblock = this.props.history.block((prompt): string | undefined => {
      if (
        this.ordersStore.hasUnsavedChanges && 
        prompt.pathname !== this.props.location.pathname && 
        this.authStore.loggedIn
      ) {
        return 'Are you sure you want to leave? All changes will be lost.';
      }
    });
  }

  private handleStepChange = (step: StepNumber) => {
    this.ordersStore.setOrderCreationStep(step);
  }

  private handleCreate = async () => {
    if (this.selectedDelis === undefined)
    {
      showNotification('Missing tasks.  Please click the "Show details" button on the Deliverable field and setup the tasks.', NotificationType.error);
      return;
    }
    runInAction(() => {
      this.isSubmitting = true;
    });

    try {
      await this.ordersStore.createOrder(this.selectedDelis);
      showNotification('Order has been successfully created.');
      this.close();
    } 
    catch (err) {
      runInAction(() => {
        this.isSubmitting = false;
      });

      showNotification(`Failed to create order: ${errObj2Str(err)}.`, NotificationType.error);
    }
  }

  private close = () => {
    history.push({
      pathname: ROUTES.private.orders,
      search: getBreadCrumbs(ROUTES.private.orders)?.search
    });
  }

  private get stepContent() {
    const steps = {
      [StepNumber.orderDetails]: <OrderDetails 
        showTasks={this.handleShowTasks.bind(this)} 

      />,
      [StepNumber.assetsDeliverables]: (
        <AssetDeliverables 
          initializeOrderFormLeaveConfirmation={this.initializeLeaveConfirmation} 
        />
      ),
    };

    return steps[this.ordersStore.createOrderStep];
  }

  @action handleShowTasks = () => {
    this.showTasks = true;
  }

  @action handleSubmitTasks = (selectedDelis: OrderTask[]) => {
    this.showTasks = false;
    this.selectedDelis = selectedDelis;
  }

  @action handleCloseTasks = async () => {
    console.log("**** CloseTask here.")
    this.showTasks = false;
  }

  render() {
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid
          container
          direction="column"
          className={classes.root}
          justifyContent="center"
          alignItems="center"
        >
          <Stepper
            firstStep={FIRST_STEP}
            lastStep={LAST_STEP}
            steps={this.steps}
            currentStep={this.ordersStore.createOrderStep}
            nextStepButtonState={{
              [StepNumber.orderDetails]: this.ordersStore.isValidOrderDetailsStep,
              [StepNumber.assetsDeliverables]: this.ordersStore.canCreateOrder
            }}
            creationLoading={this.isSubmitting}
            onStepChange={this.handleStepChange}
            onCreate={this.handleCreate}
            onTerminate={this.close}
          />
          <div className={classes.content}>
            {this.stepContent}
          </div>
        </Grid>

        {/* Place popup dialogs below. */}
        <Grid container spacing={0} style={{width: 500}}>
          { (this.showTasks) && (  // Show Tasks dialog
            <Tasks
              onClose={this.handleCloseTasks.bind(this)}
              onSubmit={this.handleSubmitTasks.bind(this)}
            />
          )}
        </Grid>
      </Grid>
    );
  }
}

const OrderCreatingStyled = withStyles(
  OrderCreating, 
  (theme, props) => ({
    "root": {},
    "content": {
      marginTop: 70,
      minWidth: 540,
    }
  })
);

export default OrderCreatingStyled;